import React from 'react'
import PropTypes from 'prop-types'
import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Table, Tag } from 'antd'
import dayjs from 'dayjs'

const camelize = (value) => value.replace(/_([a-z])/g, (_match, letter) => letter.toUpperCase())

const formatDateTime = (datetime) => (datetime && datetime.raw ? dayjs(datetime.raw).format('DD/MM/YYYY HH:mm') : '-')

const formatMoney = (money) => (money && money.display ? money.display : '-')

const renderList = (values) => (
  values && values.length ? values.map((value) => <div key={value}>{value}</div>) : '-'
)

const getColumns = (t) => [
  {
    key: 'id',
    title: t('columns.id'),
    dataIndex: 'id',
    fixed: 'left',
    width: 90
  },
  {
    key: 'paymentTime',
    title: t('columns.paymentTime'),
    dataIndex: 'paymentTime',
    width: 140,
    render: formatDateTime
  },
  {
    key: 'name',
    title: t('columns.name'),
    render: (row) => (
      <div style={{ minWidth: 200 }}>
        {row.name || '-'}
        {row.isRequestItem && <Tag color='orange' style={{ marginLeft: 8 }}>{t('requestItem')}</Tag>}
      </div>
    )
  },
  {
    key: 'orderNumber',
    title: t('columns.orderNumber'),
    dataIndex: 'orderNumber',
    render: (number) => (number ? <Link to={`/inquiries/${number}`}>{number}</Link> : '-')
  },
  {
    key: 'customerNo',
    title: t('columns.customerNo'),
    dataIndex: 'customerNo',
    render: (customerNo) => (customerNo ? <Link to={`/customers/${customerNo}`}>{customerNo}</Link> : '-')
  },
  {
    key: 'customerName',
    title: t('columns.customerName'),
    dataIndex: 'customerName',
    render: (name) => name || '-'
  },
  {
    key: 'customerPhoneNumber',
    title: t('columns.customerPhoneNumber'),
    dataIndex: 'customerPhoneNumber',
    render: (phone) => phone || '-'
  },
  {
    key: 'quantity',
    title: t('columns.quantity'),
    align: 'right',
    render: (row) => (row.quantity === null || row.quantity === undefined ? '-' : `${row.quantity}${row.unit ? ` ${row.unit}` : ''}`)
  },
  {
    key: 'cost',
    title: t('columns.cost'),
    dataIndex: 'cost',
    align: 'right',
    render: formatMoney
  },
  {
    key: 'singlePrice',
    title: t('columns.singlePrice'),
    dataIndex: 'singlePrice',
    align: 'right',
    render: formatMoney
  },
  {
    key: 'truckOriginAddresses',
    title: t('columns.truckOriginAddresses'),
    dataIndex: 'truckOriginAddresses',
    render: (addresses) => <div style={{ minWidth: 180 }}>{renderList(addresses)}</div>
  },
  {
    key: 'orderState',
    title: t('columns.orderState'),
    dataIndex: 'orderState',
    render: (state) => (state ? t(`states.${camelize(state)}`, { defaultValue: state }) : '-')
  },
  {
    key: 'shippingDistrict',
    title: t('columns.shippingDistrict'),
    dataIndex: 'shippingDistrict',
    render: (district) => district || '-'
  },
  {
    key: 'shippingProvince',
    title: t('columns.shippingProvince'),
    dataIndex: 'shippingProvince',
    render: (province) => province || '-'
  },
  {
    key: 'supplierNames',
    title: t('columns.supplierNames'),
    dataIndex: 'supplierNames',
    render: renderList
  },
  {
    key: 'productCategoryName',
    title: t('columns.productCategoryName'),
    dataIndex: 'productCategoryName',
    render: (name) => name || '-'
  },
  {
    key: 'updatedAt',
    title: t('columns.updatedAt'),
    dataIndex: 'updatedAt',
    width: 140,
    render: formatDateTime
  }
]

const LineItemsTable = ({ loading, rows, page, pageSize, total, onPageChange }) => {
  const { t } = useTranslation('lineItems')

  return (
    <Table
      rowKey='id'
      size='small'
      scroll={{ x: true }}
      loading={loading}
      columns={getColumns(t)}
      dataSource={rows}
      pagination={{
        current: page,
        pageSize,
        total,
        showSizeChanger: false,
        showTotal: (count) => t('total', { count }),
        onChange: onPageChange
      }}
    />
  )
}

LineItemsTable.propTypes = {
  loading: PropTypes.bool.isRequired,
  rows: PropTypes.arrayOf(PropTypes.object).isRequired,
  page: PropTypes.number.isRequired,
  pageSize: PropTypes.number.isRequired,
  total: PropTypes.number.isRequired,
  onPageChange: PropTypes.func.isRequired
}

export default LineItemsTable
